import type { StrikeTotalResponse } from '../api/client'

/**
 * Display-state matrix for the HoldingsPanel TOTAL row when the C# strike
 * anchor is promoted (GGE strike-anchor promotion arc, Task 5).
 *
 * Pure: no I/O, never throws. `deriveStrikeTotal` evaluates in order:
 *   1. not promoted (null, !available, mode !== 'shadow')  -> NOT_PROMOTED
 *   2. stale `asOfUtc` (older than STRIKE_TOTAL_STALE_MINUTES, missing or
 *      unparseable)                                         -> amber, wins over source
 *   3. source switch: since_strike (green, amber when live weight is low),
 *      pre_strike_daily (amber), prior_session_final (grey),
 *      anchor_missing (red, no value).
 */
export const STRIKE_TOTAL_STALE_MINUTES = 15
export const LOW_LIVE_WEIGHT_FRAC = 0.05

export type StrikeTotalBadge = 'green' | 'amber' | 'grey' | 'red'

export interface StrikeTotalView {
  promoted: boolean
  returnFrac: number | null
  caption: string
  badge: StrikeTotalBadge | null
}

export const NOT_PROMOTED: StrikeTotalView = Object.freeze({
  promoted: false,
  returnFrac: null,
  caption: '',
  badge: null,
})

const hmFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

const dateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

/** 'HH:MM' in Europe/London; '' when unparseable. */
export function formatUkHm(isoUtc: string): string {
  const d = parse(isoUtc)
  return d ? hmFormatter.format(d) : ''
}

function formatUkDate(isoUtc: string | null | undefined): string {
  const d = parse(isoUtc)
  return d ? dateFormatter.format(d) : ''
}

export function deriveStrikeTotal(
  resp: StrikeTotalResponse | null | undefined,
  nowUtc: Date,
): StrikeTotalView {
  if (!resp || !resp.available || resp.mode !== 'shadow') return NOT_PROMOTED

  const returnFrac = typeof resp.returnFrac === 'number' && Number.isFinite(resp.returnFrac) ? resp.returnFrac : null

  const asOf = parse(resp.asOfUtc)
  const ageMin = asOf ? (nowUtc.getTime() - asOf.getTime()) / 60000 : Number.POSITIVE_INFINITY
  if (ageMin > STRIKE_TOTAL_STALE_MINUTES) {
    const hm = asOf ? formatUkHm(resp.asOfUtc as string) : ''
    return {
      promoted: true,
      returnFrac,
      caption: hm ? `Strike total stale — last ${hm} UK` : 'Strike total stale — no timestamp',
      badge: 'amber',
    }
  }

  switch (resp.source) {
    case 'since_strike': {
      const hm = formatUkHm(resp.strikeTimeUtc ?? '')
      const low = typeof resp.liveWeightFrac === 'number' && resp.liveWeightFrac < LOW_LIVE_WEIGHT_FRAC
      return {
        promoted: true,
        returnFrac,
        caption: hm ? `Since ${hm} UK` : 'Since strike',
        badge: low ? 'amber' : 'green',
      }
    }
    case 'pre_strike_daily':
      return { promoted: true, returnFrac, caption: 'Daily r_kt — pre-strike (includes T-1 lag)', badge: 'amber' }
    case 'prior_session_final': {
      const date = formatUkDate(resp.strikeTimeUtc)
      return { promoted: true, returnFrac, caption: date ? `Final for ${date}` : 'Final', badge: 'grey' }
    }
    case 'anchor_missing':
      return { promoted: true, returnFrac: null, caption: 'Strike anchor missing — see K_t tile', badge: 'red' }
    default:
      return NOT_PROMOTED
  }
}
